import React from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { communityInvitePropertyTypeIcons, customFieldTypeIcons } from '../../../utils/iconUtils';
import {
  communityInvitePropertyTypeToString,
  customFieldTypeToString,
} from '../../../utils/stringUtils';
import { CustomFieldType } from '../../../models/inviteService.model';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';

type SelectExportedFieldsProps = {
  selectedFields: (string | number)[];
  setSelectedFields: React.Dispatch<React.SetStateAction<(string | number)[]>>;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function SelectExportedFields({
  selectedFields,
  setSelectedFields,
  className,
  ...props
}: SelectExportedFieldsProps): JSX.Element {
  const community = useSelectedCommunity();

  const toggleSelected = (field: string | number) => {
    setSelectedFields((prev) => {
      if (prev.includes(field)) {
        return prev.filter((f) => f !== field);
      }
      return [...prev, field];
    });
  };

  if (!community) {
    return <></>;
  }

  const profileFields = [
    ...(community.inviteFields?.mandatoryFields ?? []),
    ...(community.inviteFields?.optionalFields ?? []),
  ];

  const customFields = [...(community.inviteFields?.customFields ?? [])];

  return (
    <div className={twMerge('flex flex-col gap-2', className)} {...props}>
      {profileFields.map((field) => (
        <Field
          key={field}
          selected={selectedFields.includes(field)}
          toggleSelected={() => toggleSelected(field)}
          icon={
            communityInvitePropertyTypeIcons[field as keyof typeof communityInvitePropertyTypeIcons]
          }
          label={communityInvitePropertyTypeToString(field)}
        />
      ))}
      {customFields.map((field) =>
        field.customFieldId !== undefined ? (
          <CustomField
            key={field.customFieldId}
            selected={selectedFields.includes(field.customFieldId)}
            toggleSelected={() => toggleSelected(field.customFieldId as number)}
            label={customFieldTypeToString(field)}
            type={field.type}
          />
        ) : null,
      )}
    </div>
  );
}

type FieldProps = {
  selected: boolean;
  toggleSelected: () => void;
  icon: JSX.Element;
  label: string;
  children?: JSX.Element;
};

function Field({ selected, toggleSelected, icon, label, children }: FieldProps): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div
        className={classNames(
          'flex w-full cursor-pointer items-center justify-between rounded-lg border border-secondary-200 px-2 py-[3px] transition-all',
          {
            'bg-secondary-200': selected,
          },
        )}
        onClick={toggleSelected}>
        <div className="flex w-full items-center gap-2">
          <div className="flex items-center justify-center rounded-[6px] bg-secondary p-1 text-secondary-50">
            {icon}
          </div>
          <p className="whitespace-nowrap py-2 text-sm first-letter:uppercase">{label}</p>
          {children}
        </div>
        <input
          type="checkbox"
          checked={selected}
          className="m-1 rounded-full border-secondary-200"
        />
      </div>
    </div>
  );
}

type CustomFieldProps = {
  selected: boolean;
  toggleSelected: () => void;
  label: string;
  type: CustomFieldType;
  children?: JSX.Element;
};

function CustomField({
  selected,
  toggleSelected,
  label,
  type,
  children,
}: CustomFieldProps): JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div
        className={classNames(
          'flex w-full cursor-pointer items-center justify-between rounded-lg border border-secondary-200 px-2 py-[3px] transition-all',
          {
            'bg-secondary-200': selected,
          },
        )}
        onClick={toggleSelected}>
        <div className="flex w-full items-center gap-2">
          <div className="flex items-center justify-center rounded-[6px] bg-secondary p-1 text-secondary-50">
            {customFieldTypeIcons[type]}
          </div>
          <p className="whitespace-nowrap py-2 text-sm first-letter:uppercase">{label}</p>
          {children}
        </div>
        <input
          type="checkbox"
          checked={selected}
          className="m-1 rounded-full border-secondary-200"
        />
      </div>
    </div>
  );
}
